import { FeatureType } from "src/clients/features-control";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import { generateFeatureMetadata } from "src/featureCatalogue/Queries/Create/utils";
import { UseFeatureQueryPreview } from "src/featureCatalogue/queries";

export type ColumnType = "entity" | "feature" | "grouping_key";

export const MIN_COLUMNS_FOR_NON_GLOBAL_FEATURES = 2;
export const MIN_COLUMNS_FOR_GROUPING_KEY_WITH_ENTITY = 3;
export const MIN_COLUMNS_FOR_GROUPING_KEY_WITHOUT_ENTITY = 2;

export type PreviewData = NonNullable<
  UseFeatureQueryPreview["previewData"]
>["data"];

export const autoAssignColumnTypes = (
  previewData: PreviewData,
  existingTypes?: Record<string, ColumnType>,
  hasEntity: boolean = true,
): Record<string, ColumnType> => {
  if (!previewData?.length) return {};

  const columnNames = Object.keys(previewData[0] ?? {});
  if (columnNames.length === 0) return {};

  // Honor saved/explicit types; default unassigned columns to "feature".
  const seeded: Record<string, ColumnType> = Object.fromEntries(
    columnNames.map((column) => [column, existingTypes?.[column] ?? "feature"]),
  );

  if (!hasEntity) {
    // Strip any stale "entity" assignments and ensure no entity exists.
    return Object.fromEntries(
      Object.entries(seeded).map(([col, type]) => [
        col,
        type === "entity" ? "feature" : type,
      ]),
    );
  }

  // Find an existing entity. If none was provided by existingTypes, default to
  // the first preview column. If multiple were provided (shouldn't happen — the
  // schema enforces max 1), keep the first and demote the rest.
  const entityColumns = Object.entries(seeded)
    .filter(([, type]) => type === "entity")
    .map(([col]) => col);

  if (entityColumns.length === 0) {
    seeded[columnNames[0]] = "entity";
    return seeded;
  }

  return Object.fromEntries(
    Object.entries(seeded).map(([col, type]) => [
      col,
      type === "entity" && col !== entityColumns[0] ? "feature" : type,
    ]),
  );
};

export const getEntityColumnFromTypes = (
  columnTypes: Record<string, ColumnType>,
) => {
  const entityColumns = Object.entries(columnTypes)
    .filter(([_, type]) => type === "entity")
    .map(([column]) => column);

  return entityColumns[0];
};

export const handleFeatureGenerationFromColumnTypes = (
  columnTypes: Record<string, ColumnType>,
  existingFeatures: QueryFormValues["columns"]["features"],
  windowLength: number | null,
  featureDatatype?: Record<string, FeatureType | undefined>,
  // When true, regenerate `feature_key` and `name` from the new window for
  // existing features as well — used only when the window length itself
  // changed. Other triggers preserve user-set keys/names per column_name.
  windowChanged: boolean = false,
) => {
  const featureColumns = Object.entries(columnTypes)
    .filter(([_, type]) => type === "feature")
    .map(([column]) => column);

  const matchedFeatures = featureColumns.map((col) => {
    const { featureKey, featureName } = generateFeatureMetadata(
      col,
      windowLength ?? undefined,
    );

    const existingFeature = existingFeatures?.find(
      (f) => f.column_name === col,
    );

    if (existingFeature) {
      // `feature_type` is derived from preview data, not user-set, so it
      // must refresh when the SQL changes the inferred type (e.g. a new
      // CAST). Falling back to the saved value keeps the field stable when
      // the preview hasn't run yet or the inference is missing.
      const refreshedFeatureType =
        featureDatatype?.[col] ?? existingFeature.feature_type;
      if (windowChanged) {
        // User changed the window — refresh the auto-generated key/name so
        // they reflect the new window. `feature_default` and `description`
        // survive via the spread.
        return {
          ...existingFeature,
          column_name: col,
          feature_key: featureKey,
          name: featureName,
          feature_type: refreshedFeatureType,
        };
      }
      // Per-column-name preservation: a feature already exists for this
      // column, so its user-set fields (`feature_key`, `name`,
      // `feature_default`, `description`) are frozen — they may be
      // referenced by deployed Decision Flows and Jobs.
      return {
        ...existingFeature,
        column_name: col,
        feature_type: refreshedFeatureType,
      };
    }

    return {
      column_name: col,
      feature_key: featureKey,
      name: featureName,
      feature_type: featureDatatype?.[col],
      feature_default: "None",
      description: "",
    };
  });

  // Keep features whose column_name no longer appears in the preview at all
  // (column was removed from SQL). They stay in form data so they can come
  // back automatically if the column is restored. Features whose column was
  // reassigned to entity/grouping_key (still in columnTypes but not as a
  // feature) are intentionally dropped here.
  // On a window change, regenerate orphan key/name too — otherwise a column
  // restored after a window change would resurface with a stale window
  // suffix (e.g. `_1h` while the window is now `1d`).
  const allExistingFeatures: NonNullable<
    QueryFormValues["columns"]["features"]
  > = existingFeatures ?? [];
  const orphanFeatures = allExistingFeatures
    .filter((f) => !Object.hasOwn(columnTypes, f.column_name))
    .map((f) => {
      if (!windowChanged) return f;
      const { featureKey, featureName } = generateFeatureMetadata(
        f.column_name,
        windowLength ?? undefined,
      );
      return { ...f, feature_key: featureKey, name: featureName };
    });

  // Invariant: orphans MUST be tail-placed (after all matched features).
  // The validation resolver in ManageFeatureQueryPage filters orphans out of
  // the array passed to yup, and yup reports errors by array index. As long
  // as matched features occupy indices [0..N-1], filtered-index `i` equals
  // form-state-index `i` and errors land on the right feature. If this
  // ordering ever changes (e.g. interleaving orphans, sorting alphabetically),
  // the resolver must remap yup error paths back to original indices.
  return [...matchedFeatures, ...orphanFeatures];
};
